(() => {
  // ---------- tiny helpers ----------
  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const mrm = () => window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  // visibility check
  function isVisible(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect?.();
    if (!rect || (rect.width === 0 && rect.height === 0)) return false;
    const cs = getComputedStyle(el);
    return !(cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') < 0.01);
  }

  // Read theme tokens if present; fall back to sensible defaults
  function readToken(name, fallback) {
    const cs = getComputedStyle(document.documentElement);
    const raw = cs.getPropertyValue(name).trim();
    if (!raw) return fallback;
    if (/ms$|s$/.test(raw)) {
      if (raw.endsWith('ms')) return parseFloat(raw);
      if (raw.endsWith('s')) return parseFloat(raw) * 1000;
    }
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  }
  function readBezier(name, fallback) {
    const cs = getComputedStyle(document.documentElement);
    const raw = cs.getPropertyValue(name).trim();
    return raw || fallback;
  }

  // Collect cart-related section IDs so Shopify returns rendered HTML we can hand to the theme
  function getCartSectionIds() {
    const ids = new Set();
    qa('cart-items-component').forEach(el => {
      const id = el?.dataset?.sectionId;
      if (id) ids.add(id);
    });
    qa('[data-section-id][data-cart-section]').forEach(el => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  // Dispatch your theme's events so components like <cart-items-component> morph themselves
  function dispatchThemeCartEvents(json) {
    const TE = window.ThemeEvents || {};
    const detail = { data: json, sections: json.sections, itemCount: json.item_count, source: 'dl-hero' };
    try { if (window.CartAddEvent) document.dispatchEvent(new window.CartAddEvent({}, null, detail)); } catch (_) {}
    document.dispatchEvent(new CustomEvent(TE.cartAdd || 'cart:add',       { detail }));
    document.dispatchEvent(new CustomEvent(TE.cartUpdate || 'cart:update', { detail }));
  }

  // Prefer explicit marker; then overrides; then common selectors; else fallback
  function findCartTarget() {
    let el = document.querySelector('[data-fly-to-cart-target], [data-fly-cart-target]');
    if (isVisible(el)) return { el, rect: el.getBoundingClientRect() };

    const bodySel = document.body?.dataset?.flyToCartTarget;
    if (bodySel) { el = document.querySelector(bodySel); if (isVisible(el)) return { el, rect: el.getBoundingClientRect() }; }

    const cssSel = getComputedStyle(document.documentElement).getPropertyValue('--fly-to-cart-target').trim();
    if (cssSel) { el = document.querySelector(cssSel); if (isVisible(el)) return { el, rect: el.getBoundingClientRect() }; }

    const candidates = [
      '[data-cart-toggle]',
      'button[aria-controls="cart-drawer"]',
      '.action__cart',
      '.header__icon--cart',
      'a[href*="/cart"]',
      '.cart-toggle',
      '.js-cart-toggle',
      '.site-header__cart',
      'cart-toggle',
      '[data-action="open-cart"]',
      '.header__cart, .Header__Cart, .cart-icon'
    ];
    for (const s of candidates) {
      el = document.querySelector(s);
      if (isVisible(el)) return { el, rect: el.getBoundingClientRect() };
    }

    return { el: null, rect: { left: window.innerWidth - 24, top: 16, width: 0, height: 0 } };
  }

  // ---- cubic-bezier easing helper (for animating CSS variables with rAF) ----
  function cubicBezier(p0x, p0y, p1x, p1y) {
    const NEWTON_ITERATIONS = 4, NEWTON_MIN_SLOPE = 0.001, SUBDIVISION_PRECISION = 0.0000001, SUBDIVISION_MAX_ITERATIONS = 10;
    const kSplineTableSize = 11, kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);
    const float32ArraySupported = typeof Float32Array === 'function';
    const sampleValues = float32ArraySupported ? new Float32Array(kSplineTableSize) : new Array(kSplineTableSize);
    function A(aA1, aA2) { return 1.0 - 3.0 * aA2 + 3.0 * aA1; }
    function B(aA1, aA2) { return 3.0 * aA2 - 6.0 * aA1; }
    function C(aA1) { return 3.0 * aA1; }
    function calcBezier(aT, aA1, aA2) { return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT; }
    function getSlope(aT, aA1, aA2) { return 3.0 * A(aA1, aA2) * aT * aT + 2.0 * B(aA1, aA2) * aT + C(aA1); }
    for (let i = 0; i < kSplineTableSize; ++i) sampleValues[i] = calcBezier(i * kSampleStepSize, p0x, p1x);
    function getTForX(aX) {
      let intervalStart = 0.0, currentSample = 1, lastSample = kSplineTableSize - 1;
      for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) intervalStart += kSampleStepSize;
      --currentSample;
      const dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
      let guessForT = intervalStart + dist * kSampleStepSize;
      const initialSlope = getSlope(guessForT, p0x, p1x);
      if (initialSlope >= NEWTON_MIN_SLOPE) {
        for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
          const currentSlope = getSlope(guessForT, p0x, p1x);
          if (currentSlope === 0.0) return guessForT;
          const currentX = calcBezier(guessForT, p0x, p1x) - aX;
          guessForT -= currentX / currentSlope;
        }
        return guessForT;
      } else if (initialSlope === 0.0) {
        return guessForT;
      } else {
        let a = intervalStart, b = intervalStart + kSampleStepSize, currentX, currentT, i = 0;
        do {
          currentT = a + (b - a) / 2.0;
          currentX = calcBezier(currentT, p0x, p1x) - aX;
          if (currentX > 0.0) b = currentT; else a = currentT;
        } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
        return currentT;
      }
    }
    return function BezierEasing(x) {
      if (p0x === p0y && p1x === p1y) return x;
      return calcBezier(getTForX(x), p0y, p1y);
    };
  }

  // ---------- NATIVE fly-to-cart bubble (uses <fly-to-cart> + theme tokens + arc path) ----------
  function flyToCart(imgEl) {
    try {
      if (!imgEl || mrm()) return; // respect prefers-reduced-motion

      // Ensure <fly-to-cart> singleton exists
      let bubble = document.querySelector('fly-to-cart');
      if (!bubble) {
        bubble = document.createElement('fly-to-cart');
        document.body.appendChild(bubble);
      }

      // Source image (background of the bubble)
      const src = imgEl.currentSrc || imgEl.src;
      if (src) bubble.style.backgroundImage = `url("${src}")`;

      // Measure start (image center) and target (cart center)
      const imgRect = imgEl.getBoundingClientRect();
      const target = findCartTarget();
      const cartRect = target.rect;

      const SIZE = 40;
      const startLeft = imgRect.left + (imgRect.width / 2) - (SIZE / 2);
      const startTop  = imgRect.top  + (imgRect.height / 2) - (SIZE / 2);
      const endLeft   = (cartRect.left + cartRect.width / 2) - (SIZE / 2);
      const endTop    = (cartRect.top  + cartRect.height / 2) - (SIZE / 2);

      // Place bubble at start
      bubble.style.left = `${startLeft}px`;
      bubble.style.top  = `${startTop}px`;
      bubble.style.setProperty('--x', '0px');
      bubble.style.setProperty('--y', '0px');
      bubble.style.setProperty('--scale', '1');
      bubble.style.opacity = '1';

      // Read theme timing/easing tokens if available
      const dur = readToken('--duration-fast', 600); // ms
      const easeStr = readBezier('--bezier-out', 'cubic-bezier(0.22, 1, 0.36, 1)');
      const m = easeStr.match(/cubic-bezier\s*\(\s*([0-9\.\-e]+)\s*,\s*([0-9\.\-e]+)\s*,\s*([0-9\.\-e]+)\s*,\s*([0-9\.\-e]+)\s*\)/i);
      const ease = m ? cubicBezier(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]), parseFloat(m[4])) : (t=>t);

      // Create a gentle arc upwards
      const dx = endLeft - startLeft;
      const dy = endTop  - startTop;
      const distance = Math.hypot(dx, dy);
      const arc = Math.min(120, Math.max(40, distance * 0.2));
      const cpX = startLeft + dx * 0.5;
      const cpY = Math.min(startTop, endTop) - arc;

      // Quadratic Bezier interpolation
      const x0 = 0, y0 = 0;
      const x1 = cpX - startLeft, y1 = cpY - startTop;
      const x2 = dx, y2 = dy;

      const start = performance.now();
      const scaleStart = 1, scaleEnd = 0.2;

      function frame(now) {
        const t = Math.min(1, (now - start) / dur);
        const e = ease(t);

        const omt = 1 - e;
        const bx = omt*omt*x0 + 2*omt*e*x1 + e*e*x2;
        const by = omt*omt*y0 + 2*omt*e*y1 + e*e*y2;

        bubble.style.setProperty('--x', `${bx}px`);
        bubble.style.setProperty('--y', `${by}px`);
        const sc = scaleStart + (scaleEnd - scaleStart) * e;
        bubble.style.setProperty('--scale', String(sc));

        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          bubble.style.opacity = '0';
          setTimeout(() => {
            bubble.style.backgroundImage = '';
            bubble.style.setProperty('--x', '0px');
            bubble.style.setProperty('--y', '0px');
            bubble.style.setProperty('--scale', '1');
          }, 350);
        }
      }
      requestAnimationFrame(frame);
    } catch (_) {}
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  async function updateCartBadge() {
    try {
      const cart = await fetchCart();
      const selectors = [
        '[data-cart-count]',
        '.cart-count-bubble',
        '.header__cart-count',
        '.cart-count',
        '.cart-bubble__count',
        '.js-cart-count',
        '.header-cart-count',
        '[data-cart-count-bubble]'
      ];
      const nodes = selectors.flatMap(s => qa(s));
      nodes.forEach(n => {
        if (n.hasAttribute('data-cart-count')) n.setAttribute('data-cart-count', cart.item_count);
        n.textContent = String(cart.item_count);
        n.hidden = false;
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      document.dispatchEvent(new CustomEvent('cart:change',  { detail: cart }));
    } catch (_) {}
  }

  function openCartUI() {
    const toggles = qa('[data-cart-toggle], .cart-toggle, .js-cart-toggle, button[aria-controls="cart-drawer"]');
    if (toggles[0]) toggles[0].click();
    const drawer = q('cart-drawer');
    if (drawer) { try { drawer.setAttribute('open',''); if (typeof drawer.open === 'function') drawer.open(); } catch(_){} }
    document.body.classList.add('cart-open', 'js-drawer-open');
    document.dispatchEvent(new CustomEvent('cart:toggle', { detail: { open: true } }));
  }

  // ---------- Benefits (no animation dependency) ----------
  function setBenefits(container, data, color) {
    if (!container || !data) return;
    const root = container.closest('.dl-hero');
    if (root && color) root.style.setProperty('--benefit-color', color);
    const fields = container.querySelectorAll('[data-split-text], [data-decrypt-text]');
    const texts = [data.k1, data.l1, data.k2, data.l2, data.k3, data.l3];
    texts.forEach((txt, i) => { const el = fields[i]; if (el) el.textContent = txt || ''; });
  }

  // ---------- Carousel per section ----------
  function initHero(section) {
    if (!section || !section.id || !section.id.startsWith('product-hero-')) return;
    const viewport = q('[data-carousel]', section);
    const track = q('[data-track]', section);
    const slides = qa('[data-slide]', section);
    if (!viewport || !track || !slides.length) return;

    const dotsWrap = q('[data-dots]', section) || q('[class*="ai-new-products__dots-"]', section);
    const prev     = q('[data-prev]', section) || q('[class*="ai-new-products__nav-prev-"]', section);
    const next     = q('[data-next]', section) || q('[class*="ai-new-products__nav-next-"]', section);
    const benefits = q('[data-benefits]', section);

    let dots = [];
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      dots = slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => goTo(i, true));
        dotsWrap.appendChild(b);
        return b;
      });
    }

    let idx = 0;
    const autoAttr = section.dataset.interval || '6';
    const n = parseInt(autoAttr, 10);
    const interval = Math.max(Number.isNaN(n) ? 6 : n, 3) * 1000;
    const auto = section.dataset.auto === 'true';
    let timer = null, isHover = false;

    function updateUI() {
      track.style.transform = `translateX(-${idx * 100}%)`;
      dots.forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      const s = slides[idx];
      const data = { k1: s.dataset.k1, l1: s.dataset.l1, k2: s.dataset.k2, l2: s.dataset.l2, k3: s.dataset.k3, l3: s.dataset.l3 };
      setBenefits(benefits, data, s.dataset.accent);
      section.__dlHeroIndex = idx;
    }
    function goTo(i, user) { idx = (i + slides.length) % slides.length; updateUI(); if (user) resetAuto(); }
    const nextSlide = () => goTo(idx + 1, false);
    const prevSlide = () => goTo(idx - 1, false);

    prev && prev.addEventListener('click', () => goTo(idx - 1, true));
    next && next.addEventListener('click', () => goTo(idx + 1, true));

    // touch
    let startX = 0, dx = 0, touching = false;
    viewport.addEventListener('touchstart', e => { touching = true; startX = e.touches[0].clientX; clearInterval(timer); }, { passive: true });
    viewport.addEventListener('touchmove',  e => { if (!touching) return; dx = e.touches[0].clientX - startX; }, { passive: true });
    viewport.addEventListener('touchend',   () => { touching = false; if (Math.abs(dx) > 40) (dx < 0 ? nextSlide() : prevSlide()); resetAuto(); });

    // hover pause
    viewport.addEventListener('mouseenter', () => { isHover = true; clearInterval(timer); });
    viewport.addEventListener('mouseleave', () => { isHover = false; resetAuto(); });

    function resetAuto() {
      clearInterval(timer);
      if (auto && slides.length > 1) timer = setInterval(() => { if (!isHover) nextSlide(); }, interval);
    }

    updateUI();
    resetAuto();
  }

  function initAll() { qa('[id^="product-hero-"]').forEach(initHero); }

  // lifecycle
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', e => initHero(e.target));
  document.addEventListener('shopify:section:select', e => initHero(e.target));

  // ---------- AJAX Add-to-cart for hero forms ----------
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('dl-hero__add-form')) return;

    const section = form.closest('[id^="product-hero-"]');
    if (!section) return;

    e.preventDefault();

    const btn = q('button[type="submit"]', form);
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

    try {
      const fd = new FormData(form);
      if (!fd.get('quantity')) fd.set('quantity', '1');

      // Ask Shopify to return rendered sections so your theme can morph them immediately
      const sectionIds = getCartSectionIds();
      if (sectionIds.length) {
        fd.append('sections', sectionIds.join(','));
        fd.append('sections_url', window.location.pathname);
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: fd
      });
      if (!res.ok) {
        let msg = 'Unable to add to cart.';
        try { const j = await res.json(); if (j && j.description) msg = j.description; } catch (_) {}
        throw new Error(msg);
      }

      const json = await res.json();

      // Emit theme events so <cart-items-component> updates itself
      dispatchThemeCartEvents(json);

      // Visual feedback (native bubble)
      const img = q(`.dl-hero__slide:nth-child(${(section.__dlHeroIndex || 0) + 1}) .dl-hero__media img`, section) ||
                  q('.dl-hero__media img', section);
      flyToCart(img);
      await updateCartBadge();

      // --- CHANGED: do not auto-open the drawer unless explicitly opted in ---
      const shouldAutoOpen =
        document.body.matches('[data-open-cart-on-add]') ||
        !!document.querySelector('cart-drawer-component[auto-open]');
      if (shouldAutoOpen) openCartUI();
      // ----------------------------------------------------------------------

      if (btn) { btn.classList.add('dl-pulse'); setTimeout(() => btn.classList.remove('dl-pulse'), 650); }
    } catch (err) {
      form.submit(); // fallback
      return;
    } finally {
      if (btn) { btn.disabled = false; btn.setAttribute('aria-busy', 'false'); }
    }
  }, true);

  // ---------- Tiny CSS injector: only pulse keyframes (fly bubble CSS already lives in theme) ----------
  (function ensureCSS(){
    if (q('#dl-hero-inline-css')) return;
    const style = document.createElement('style');
    style.id = 'dl-hero-inline-css';
    style.textContent = `
      .dl-pulse{ animation: dl-pulse .6s ease; }
      @keyframes dl-pulse{ 0%{transform:scale(1);} 50%{transform:scale(1.03);} 100%{transform:scale(1);} }
    `;
    document.head.appendChild(style);
  })();
})();
